import React, { useState } from 'react';
import { useTranslations } from 'use-intl';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { useSupabaseStore } from '../supabaseStore';
import { useChatStore } from '../useChat';

interface PreapproveModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const PreapproveModal: React.FC<PreapproveModalProps> = ({ isOpen, onClose }) => {
	const t = useTranslations('Support');
	const supabase = useSupabaseStore((state) => state.supabase);
	const { selectedChat } = useChatStore();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');

	const handlePreapprove = async () => {
		if (!supabase || !selectedChat?.id) return;
		setIsLoading(true);
		setError('');
		try {
			const { data, error: preApproveError } = await supabase.functions.invoke(
				'chat-customer-support-status-transition',
				{
					body: {
						chatId: selectedChat.id,
						event: 'CUSTOMER_SUPPORT_PREAPPROVES_CHAT',
						metadata: {
							preapprovedAt: new Date().toISOString()
						}
					}
				}
			);
			if (preApproveError) throw preApproveError;
			if (data && !data.success) {
				throw new Error(data.error || 'Unknown error occurred');
			}
			handleClose();
		} catch (err) {
			console.error('Error preapproving chat:', err);
			setError(t('ErrorPreapprobingChat') || 'Error preapproving chat');
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		onClose();
		setError('');
	};

	return (
		<ConfirmModal
			isOpen={isOpen}
			onClose={handleClose}
			title={t('PreapproveTitle')}
			subtitle={t('PreapproveDescription')}
			confirmActionText={t('PreapproveAction')}
			onConfirm={handlePreapprove}
			onCancel={handleClose}
			confirmBtnLoading={isLoading}
			confirmBtnTestId="preapproveConfirmBtn"
			cancelBtnTestId="preapproveCancelBtn"
			type="confirm"
		>
			{error && <div className="mt-4 text-red-500 text-sm">{error}</div>}
		</ConfirmModal>
	);
};

export default PreapproveModal;
